import {Component, Host, Input, OnInit, ViewChild} from '@angular/core';
import { CoOwnerConfig } from './co-owner.config';
import { FormConfig } from '../../../../models/form-config';
import { FormFactoryService } from '../../../../services/form-factory.service';
import { AbstractControl, FormGroup, Validators } from '@angular/forms';
import { OwnerDataSectionComponent } from '../owner-data-section.component';
import { CoOwnerModel } from '../../../../../core/models/owner-data/co-owner.model';
import { OwnerDataStateService } from '../../../../../core/services/impl/section-services/owner-data/owner-data-state.service';
import { CustomMetricsModel } from '../../../../components/models/metrics/custom-metrics.model';
import { PeselService } from '../../../../services/pesel.service';
import { PredefinedValuesService } from '../../../../../core/services/impl/section-services/predefined-values/predefined-values.service';
import { PredefinedValuesStateService } from '../../../../../core/services/impl/section-services/predefined-values/predefined-values-state.service';
import { Memoize } from '../../../../utils/memoize';
import { FieldConfig } from '../../../../models/field-config';
import { SectionComponent } from '../../../../components/section/section.component';
import { isNotNullOrUndefined } from 'codelyzer/util/isNotNullOrUndefined';
import {getDrivingLicenseIssueMonths} from '../../../../utils/driving-license-years';
import * as moment from 'moment';
import {MaterialYearPickerComponent} from '../../../../components/fields/datepickers/material-datepicker/material-year-picker/material-year-picker.component';
import {isCarOne} from '../../../../../core/utils/utils';
import {RegExpService} from '../../../../services/reg-exp.service';

@Component({
  selector: 'app-co-owner',
  templateUrl: './co-owner.component.html',
  styleUrls: ['./co-owner.component.scss']
})
export class CoOwnerComponent extends CoOwnerConfig implements OnInit {

  public drivingLicenseIssueMonthCollected: boolean = true;
  public dataPresentationFields: string[];
  noDrivingLicenseAvailable: boolean = !isCarOne();

  @Input() isDirtyBean: any;
  @Input() index: number;
  @Input() coOwner: CoOwnerModel;
  @Input() coOwnerFormGroup: FormGroup;
  @Input() coOwnerFormConfig: FormConfig;
  @Input() coOwnerFormService: FormFactoryService;
  @Input() parentSection: SectionComponent;

  @ViewChild('drivingLicenseIssueYearField') drivingLicenseIssueYearField: MaterialYearPickerComponent;

  constructor(@Host() public parent: OwnerDataSectionComponent,
              public predefined: PredefinedValuesService,
              public stateService: OwnerDataStateService,
              public predefinedState: PredefinedValuesStateService,
              public regExpService: RegExpService) {
    super(predefined, stateService, regExpService);
  }

  ngOnInit() {
    this.initControlChanges();
    this.updateOptionsOnValidPesel(this.stateService.ownerData.coOwners[this.index].pesel, this.coOwnerFormConfig);
    this.updateDrivingLicenseFieldAvailability();
    this.updateDrivingLicenseIssueMonthState();
    this.prepareFieldsInPreviewMode();
    this.initPostalCodeFieldValidity();
    this.updateDrivingLicenseFieldsState();
  }

  removeCoOwner(): void {
    this.parent.removeCoOwner(this.index);
  }

  customMetricsData(count): CustomMetricsModel {
    return {
      sectionKey: 'COOWNERS_DATA',
      count: count
    }
  }

  markAsDirty(event) {
    this.predefinedState.isDirtyBean = this.addIndexToKey(event);
  }

  @Memoize()
  getCoOwnerKeyWithIndex(key, index): string {
    return `${this.coOwnerFormConfig.formKey}.${key}.${index}`;
  }

  getFieldConfig(fieldKey: string): FieldConfig {
    return this.coOwnerFormService.fieldConfig(fieldKey);
  }

  getControl(key: string): AbstractControl {
    return this.coOwnerFormGroup.controls[key];
  }

  get previewMode(): boolean {
    return this.parentSection && this.parentSection.isPreviewMode;
  }

  canFieldDisplayed(fieldKey: string): boolean {
    return ((this.previewMode && this.getFieldConfig(fieldKey).displayInEditableMode) || !this.previewMode);
  }

  private initPostalCodeFieldValidity() {
    const postalCode = this.coOwnerFormGroup.controls[this.consts.POSTAL_CODE].value;
    this.validatePostalCode(postalCode);
  }

  private addIndexToKey(event) {
    return Object.keys(event).map(key => ({[key + `.${this.index}`]: true}))[0]
  }

  private initControlChanges(): void {

    this.handleControlsState(
      this.consts.FIRST_NAME,
      this.consts.LAST_NAME,
      this.consts.PESEL,
      this.consts.DRIVING_LICENSE_ISSUE_YEAR,
      this.consts.DRIVING_LICENSE_ISSUE_MONTH,
      this.consts.NO_DRIVING_LICENSE,
    );

    this.handleAddressControlsState(
      this.consts.POSTAL_CODE
    );
  }

  private handleControlsState(...controlNames: string[]): void {
    if (controlNames.length === 0) {
      return;
    }

    controlNames.map(controlName => {
      this.coOwnerFormGroup.controls[controlName].valueChanges.subscribe(value => {
        if (typeof value === 'string' || value instanceof String) {
          value = value.length ? value : null;
        }

        if (controlName === this.consts.DRIVING_LICENSE_ISSUE_YEAR) {
          if (isNotNullOrUndefined(value) && this.getControl(this.consts.NO_DRIVING_LICENSE).value) {
            this.getControl(this.consts.NO_DRIVING_LICENSE).setValue(false);
          }
          value = value ? moment(value).year() : null;
          this.coOwner.drivingLicenseIssueYear = value;
          this.updateDrivingLicenseIssueMonthState();
        }

        if (controlName === this.consts.NO_DRIVING_LICENSE) {
          this.stateService.changeCoOwnerNoDrivingLicense(this.index, value);
          this.updateDrivingLicenseFieldsState();
        } else {
          this.coOwner[controlName] = value;
        }

        if (controlName === this.consts.PESEL) {
          this.handleDrivingLicenseIssueYearChange(value);
          this.coOwner[this.consts.BIRTH_DATE] = PeselService.getBirthdate(value);
          this.drivingLicenseIssueYearField.initDateRange();
          this.updateDrivingLicenseFieldAvailability();
        }
        this.stateService.updateCalculationData();
      });
    });
  }

  private updateDrivingLicenseFieldsState(): void {
    if (this.stateService.getCoOwnerNoDrivingLicense(this.index)) {
      this.getControl(this.consts.DRIVING_LICENSE_ISSUE_YEAR).reset(null);
      this.getControl(this.consts.DRIVING_LICENSE_ISSUE_YEAR).clearValidators();

      this.disableCollectingDrivingLicenseIssueMonth();
    } else {
      this.getControl(this.consts.DRIVING_LICENSE_ISSUE_YEAR).setValidators([Validators.required]);

      if (this.stateService.shouldCollectDrivingLicenseIssueMonth(this.coOwner.drivingLicenseIssueYear)) {
        this.enableCollectingDrivingLicenseIssueMonth();
      } else {
        this.disableCollectingDrivingLicenseIssueMonth();
      }
    }

    this.getControl(this.consts.DRIVING_LICENSE_ISSUE_YEAR).updateValueAndValidity();
  }

  private disableCollectingDrivingLicenseIssueMonth(): void {
    this.drivingLicenseIssueMonthCollected = false;
    if (isNotNullOrUndefined(this.getControl(this.consts.DRIVING_LICENSE_ISSUE_MONTH))) {
      this.getControl(this.consts.DRIVING_LICENSE_ISSUE_MONTH).reset(null);
      this.getControl(this.consts.DRIVING_LICENSE_ISSUE_MONTH).clearValidators();
    }
  }

  private enableCollectingDrivingLicenseIssueMonth(): void {
    this.drivingLicenseIssueMonthCollected = true;
    if (isNotNullOrUndefined(this.getControl(this.consts.DRIVING_LICENSE_ISSUE_MONTH))) {
      this.getControl(this.consts.DRIVING_LICENSE_ISSUE_MONTH).setValidators([Validators.required]);
    }
  }

  private updateDrivingLicenseFieldAvailability(): void {
    const fieldConfig = this.getFieldConfig(this.consts.DRIVING_LICENSE_ISSUE_YEAR);
    if (isNotNullOrUndefined(fieldConfig.materialDateFieldConfig.minDate) && isNotNullOrUndefined(fieldConfig.materialDateFieldConfig.maxDate)) {
      this.getControl(this.consts.DRIVING_LICENSE_ISSUE_YEAR).enable();
    } else {
      this.getControl(this.consts.DRIVING_LICENSE_ISSUE_YEAR).disable();
    }
  }

  private async validatePostalCode(postalCode: string) {
    if (postalCode.length === 6) {
      const isValid = await this.stateService.validatePostalCode(postalCode);
      if (isValid) {
        this.getControl(this.consts.POSTAL_CODE).setErrors(null);
      } else {
        this.getControl(this.consts.POSTAL_CODE).setErrors({'nonexistentPostalCode': true});
        this.getControl(this.consts.POSTAL_CODE).markAsTouched();
      }
    }
  }

  private handleDrivingLicenseIssueYearChange(pesel: string) {
    this.updateOptionsOnValidPesel(pesel, this.coOwnerFormConfig);
    this.resetAndSetPreviousPeselValue(pesel, this.coOwnerFormGroup);
  }

  private handleAddressControlsState(...controlNames: string[]): void {
    if (controlNames.length === 0) {
      return;
    }

    controlNames.map(controlName => {
      this.coOwnerFormGroup.controls[controlName].valueChanges.subscribe(async value => {
        this.coOwner.address[controlName.slice(8)] = value;
        this.stateService.updateCalculationData();

        if (controlName === this.consts.POSTAL_CODE) {
          await this.validatePostalCode(value);
        }
      });
    });
  }

  private updateDrivingLicenseIssueMonthState(): void {
    const drivingLicenseIssueYear = this.coOwner.drivingLicenseIssueYear;
    if (this.stateService.getCoOwnerNoDrivingLicense(this.index) || !this.stateService.shouldCollectDrivingLicenseIssueMonth(this.coOwner.drivingLicenseIssueYear)) {
      this.disableCollectingDrivingLicenseIssueMonth();
    } else {
      this.enableCollectingDrivingLicenseIssueMonth();
    }

    if (this.drivingLicenseIssueMonthCollected) {
      const items = getDrivingLicenseIssueMonths(drivingLicenseIssueYear);
      this.getFieldConfig(this.consts.DRIVING_LICENSE_ISSUE_MONTH).options['items'] = items;
      this.getControl(this.consts.DRIVING_LICENSE_ISSUE_MONTH).setValidators([Validators.required]);
      
      // validate existing value
      const month = this.getControl(this.consts.DRIVING_LICENSE_ISSUE_MONTH).value;
      if (isNotNullOrUndefined(month) && !items.find(item => item.key === month)) { // month is defined but it doesn't exist in current dictionary
        this.getControl(this.consts.DRIVING_LICENSE_ISSUE_MONTH).reset();
      }
    } else {
      this.getControl(this.consts.DRIVING_LICENSE_ISSUE_MONTH).reset();
      this.getControl(this.consts.DRIVING_LICENSE_ISSUE_MONTH).clearValidators();
    }
    this.getControl(this.consts.DRIVING_LICENSE_ISSUE_MONTH).updateValueAndValidity();
  }

  private prepareFieldsInPreviewMode(): void {
    this.dataPresentationFields = [
      this.consts.FIRST_NAME,
      this.consts.LAST_NAME,
      this.consts.PESEL,
      this.consts.DRIVING_LICENSE_ISSUE_YEAR,
      this.consts.POSTAL_CODE,
    ];

    if (this.coOwner.drivingLicenseIssueMonth) {
      this.dataPresentationFields.push(this.consts.DRIVING_LICENSE_ISSUE_MONTH);
    }
  }
}
